import { config } from '../../config';
import { environmentFills, tokens } from '../../theme';
import type { LogoProps } from './types';

/**
 * The Augment Toolkit mark.
 *
 * **Inlined rather than loaded from `/brand/augment-toolkit-mark-brand.svg`**,
 * because the fills are set per environment and an `<img>` cannot be recoloured.
 * The two paths are the same ones in that file, which stays in `public/` as the
 * canonical asset for anything outside the app — a favicon, a README, a deck.
 * If one changes, change both.
 *
 * The mark is two converging streams: a solid front path and a lighter one
 * behind it. The tint pair comes from `theme/environment.ts`, which the login
 * page also reads for its background.
 */

/** The back stream, then the front one. Order matters: the front path paints over. */
const BACK_PATH =
  'M13.5111 19.4815C18.7936 44.5185 45.2063 32.963 50.4889 58V44.5185C45.2063 19.4815 18.7936 31.037 13.5111 6V19.4815Z';
const FRONT_PATH =
  'M13.5111 44.5185C18.7936 19.4815 45.2063 31.037 50.4889 6V19.4815C45.2063 44.5185 18.7936 32.963 13.5111 58V44.5185Z';

export const Logo = ({ size = 28, tone = 'environment', label }: LogoProps) => {
  const fills = environmentFills(config.environment);
  // Both streams go flat white when inverted, so the mark reads as one shape on a
  // coloured ground rather than as a tinted pair on the wrong one.
  const front = tone === 'inverse' ? tokens.text.onBrand : fills.front;
  const back = tone === 'inverse' ? tokens.text.onBrand : fills.back;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      // Decorative unless it is the only thing naming the product. See LogoProps.
      role={label === undefined ? undefined : 'img'}
      aria-label={label}
      aria-hidden={label === undefined}
      focusable={false}
    >
      <path d={BACK_PATH} fill={back} fillOpacity={tone === 'inverse' ? 0.55 : 1} />
      <path d={FRONT_PATH} fill={front} />
    </svg>
  );
};
